// 關鍵字效果處理器
// Keyword Effect Handlers

import type { GameState } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('KeywordHandlers');

/**
 * 所有關鍵字類型
 */
export type KeywordType =
  | '偷襲'      // Sneak Attack - 可攻擊後排
  | '劇毒'      // Poison - 戰鬥後對方死亡
  | '飛行'      // Flying - 可攻擊後排
  | '貫穿'      // Piercing - 傷害穿透
  | '遠程攻擊'  // Ranged - 可從後排攻擊
  | '阻擋'      // Block - 強制被攻擊
  | '破甲'      // Armor Break - 無視防禦
  | '滑行'      // Slide - 移動後攻擊
  | '禁錮'      // Imprison - 無法移動
  | '掩護'      // Cover - 保護後排
  | '消逝'      // Vanish - 使用後除外
  | '衰退'      // Decay - 每回合受傷
  | '拆卸';     // Dismantle - 破壞裝備

/**
 * 偷襲效果處理器
 * 喵喵賊核心機制
 */
export class SneakAttackHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('偷襲');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 檢查是否有偷襲關鍵字
    if (effectText.includes('偷襲')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantKeyword',
        value: '偷襲',
      });
    }

    // 解析「被賦予偷襲」
    if (effectText.includes('被賦予偷襲')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantKeyword',
        value: '偷襲',
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'grantKeyword' || action.value !== '偷襲') {
      return state;
    }

    // 如果有目標選擇器，給場上單位添加關鍵字
    const selector = action.targetSelector;
    if (!selector) return state;

    const isPlayer1 = state.activePlayerId === state.player1.id;
    const sideKey = isPlayer1 ? 'player1Side' : 'player2Side';

    const addKeywordToLine = (
      lineKey: 'frontLine' | 'backLine'
    ): typeof state.battlefield => {
      const side = state.battlefield[sideKey];
      const line = side[lineKey].map((slot) => {
        if (slot.unit) {
          const keywords = slot.unit.keywords ?? [];
          if (!keywords.includes('偷襲')) {
            return {
              ...slot,
              unit: { ...slot.unit, keywords: [...keywords, '偷襲'] },
            };
          }
        }
        return slot;
      });

      return {
        ...state.battlefield,
        [sideKey]: {
          ...side,
          [lineKey]: line,
        },
      };
    };

    // 前排戰線被賦予偷襲
    if (selector.zone === 'battlefield') {
      const newBattlefield = addKeywordToLine('frontLine');
      return { ...state, battlefield: newBattlefield };
    }

    return state;
  }
}

/**
 * 劇毒效果處理器
 */
export class PoisonHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('劇毒') || text.includes('< 劇毒 >');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「被賦予< 劇毒 >」
    const poisonMatch = effectText.match(/被賦予\s*<\s*劇毒\s*>/);
    if (poisonMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantKeyword',
        value: '劇毒',
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
        },
      });
    }

    // 自帶劇毒關鍵字
    if (effectText.includes('< 劇毒 >') && !effectText.includes('被賦予')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'hasKeyword',
        value: '劇毒',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'hasKeyword') {
      // 標記卡牌擁有此關鍵字（在戰鬥處理時使用）
      log.debug(`Card has 劇毒 keyword`);
      return state;
    }

    if (action.customHandler !== 'grantKeyword' || action.value !== '劇毒') {
      return state;
    }

    const selector = action.targetSelector;
    if (!selector) return state;

    const isPlayer1 = state.activePlayerId === state.player1.id;
    const sideKey = selector.side === 'enemy' 
      ? (isPlayer1 ? 'player2Side' : 'player1Side')
      : (isPlayer1 ? 'player1Side' : 'player2Side');

    let newState = state;
    let granted = 0;

    const grantToLine = (lineKey: 'frontLine' | 'backLine'): void => {
      const side = newState.battlefield[sideKey];
      const line = side[lineKey].map((slot) => {
        if (slot.unit) {
          const keywords = slot.unit.keywords ?? [];
          if (!keywords.includes('劇毒')) {
            granted++;
            return {
              ...slot,
              unit: { ...slot.unit, keywords: [...keywords, '劇毒'] },
            };
          }
        }
        return slot;
      });

      newState = {
        ...newState,
        battlefield: {
          ...newState.battlefield,
          [sideKey]: {
            ...newState.battlefield[sideKey],
            [lineKey]: line,
          },
        },
      };
    };

    grantToLine('frontLine');
    grantToLine('backLine');

    log.debug(`Granted 劇毒 to ${String(granted)} units`);
    return newState;
  }
}

/**
 * 通用關鍵字處理器基類
 * 用於簡單的關鍵字標記
 */
class SimpleKeywordHandler implements EffectHandler {
  private keyword: string;
  private patterns: string[];

  constructor(keyword: string, patterns: string[]) {
    this.keyword = keyword;
    this.patterns = patterns;
  }

  canHandle(text: string): boolean {
    return this.patterns.some(p => text.includes(p));
  }

  parse(effectText: string): ImmediateAction[] {
    if (this.patterns.some(p => effectText.includes(p))) {
      return [{
        type: 'custom',
        sourceCardId: '',
        customHandler: 'hasKeyword',
        value: this.keyword,
      }];
    }
    return [];
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'hasKeyword' && action.value === this.keyword) {
      log.debug(`[${this.keyword}Handler] Card has ${this.keyword} keyword`);
    }
    return state;
  }
}

/**
 * 飛行效果處理器
 */
export class FlyingHandler extends SimpleKeywordHandler {
  constructor() {
    super('飛行', ['飛行']);
  }
}

/**
 * 貫穿效果處理器
 */
export class PiercingHandler extends SimpleKeywordHandler {
  constructor() {
    super('貫穿', ['貫穿', '< 貫穿 >']);
  }
}

/**
 * 遠程攻擊效果處理器
 */
export class RangedAttackHandler extends SimpleKeywordHandler {
  constructor() {
    super('遠程攻擊', ['遠程攻擊', '< 遠程攻擊 >']);
  }
}

/**
 * 阻擋效果處理器
 */
export class BlockHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('阻擋') || text.includes('< 阻擋 >');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 自帶阻擋
    if (effectText.includes('< 阻擋 >')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'hasKeyword',
        value: '阻擋',
      });
    }

    // 被賦予阻擋
    if (effectText.includes('被賦予< 阻擋 >') || effectText.includes('被賦予 < 阻擋 >')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantKeyword',
        value: '阻擋',
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
          count: 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'hasKeyword' && action.value === '阻擋') {
      log.debug(`Card has 阻擋 keyword`);
    }
    // TODO: 實作賦予阻擋邏輯
    return state;
  }
}

/**
 * 破甲效果處理器
 */
export class ArmorBreakHandler extends SimpleKeywordHandler {
  constructor() {
    super('破甲', ['破甲']);
  }
}

/**
 * 滑行效果處理器
 */
export class SlideHandler extends SimpleKeywordHandler {
  constructor() {
    super('滑行', ['滑行']);
  }
}

/**
 * 禁錮效果處理器
 */
export class ImprisonHandler extends SimpleKeywordHandler {
  constructor() {
    super('禁錮', ['禁錮']);
  }
}

/**
 * 掩護效果處理器
 */
export class CoverHandler extends SimpleKeywordHandler {
  constructor() {
    super('掩護', ['掩護']);
  }
}

/**
 * 消逝效果處理器
 */
export class VanishHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('消逝');
  }

  parse(effectText: string): ImmediateAction[] {
    if (effectText.includes('消逝')) {
      return [{
        type: 'custom',
        sourceCardId: '',
        customHandler: 'vanish',
      }];
    }
    return [];
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'vanish') {
      // 消逝：使用後除外（從遊戲移除）
      log.debug(`Card will be exiled after use`);
    }
    // TODO: 實作除外邏輯
    return state;
  }
}

/**
 * 衰退效果處理器
 */
export class DecayHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('衰退');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 被賦予衰退
    if (effectText.includes('被賦予衰退')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantDecay',
        targetSelector: {
          zone: 'battlefield',
          side: 'enemy',
          count: 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'grantDecay') {
      const selector = action.targetSelector;
      if (selector) {
        log.debug(`Granting 衰退 to target`);
      }
    }
    // TODO: 實作衰退邏輯（每回合受傷）
    return state;
  }
}

/**
 * 拆卸效果處理器
 */
export class DismantleHandler extends SimpleKeywordHandler {
  constructor() {
    super('拆卸', ['拆卸']);
  }
}

/**
 * 通電效果處理器
 * 當具滑行的單位經過時觸發
 */
export class ElectrifyHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('通電') || text.includes('觸電');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「通電：當具滑行單位經過時，XXX」
    const electrifyMatch = effectText.match(/通電[：:](.+)/);
    if (electrifyMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'electrify',
        value: electrifyMatch[1].trim(),
      });
    }

    // 單純的「通電」標記
    if (effectText.trim() === '通電') {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'markElectrify',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'markElectrify') {
      // 標記此單位具有通電效果
      // 當滑行單位經過時會觸發
      log.debug(`標記通電效果`);
      return state;
    }

    if (action.customHandler === 'electrify') {
      // 實際觸發通電效果
      // 這通常由滑行系統呼叫
      const effectDescription = action.value as string;
      log.debug(`觸發通電效果: ${effectDescription}`);
      // TODO: 根據 effectDescription 執行具體效果
      return state;
    }

    return state;
  }
}

// 匯出所有關鍵字處理器
export const keywordHandlers = {
  SneakAttackHandler,
  PoisonHandler,
  FlyingHandler,
  PiercingHandler,
  RangedAttackHandler,
  BlockHandler,
  ArmorBreakHandler,
  SlideHandler,
  ImprisonHandler,
  CoverHandler,
  VanishHandler,
  DecayHandler,
  DismantleHandler,
  ElectrifyHandler,
};
